/**
 * "Invite someone" — POST /api/v1/users. v1 sends no email: the API returns the sign-in link, which this
 * pop-up shows once the person is added so it can be shared by hand (ADR §11.3).
 */
import { useState } from 'react';
import { ROLES, ROLE_LABEL, type CreateUserResponse, type Role } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { newIdempotencyKey, useCreateUser } from '../../lib/data';
import { Field, Modal, errorText, useToast } from '../../ui/kit';

const guessInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export function InviteModal({ onClose }: { onClose: () => void }) {
  const create = useCreateUser();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [initialsTouched, setInitialsTouched] = useState(false);
  const [role, setRole] = useState<Role | ''>('');
  const [phone, setPhone] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [done, setDone] = useState<CreateUserResponse | null>(null);

  const submit = () => {
    const local: Record<string, string> = {};
    if (!email.trim()) local.email = 'Enter their work email, for example name@amplispot.com';
    if (!name.trim()) local.name = 'Enter a name';
    if (!/^[A-Za-z]{1,3}$/.test(initials.trim())) local.initials = 'Use 1 to 3 letters for initials, for example DK';
    if (!role) local.role = 'Pick a role. It decides their menu and what they can see.';
    setErrs(local);
    setFormErr(null);
    if (Object.keys(local).length || !role) return;
    create.mutate(
      { key, body: { email: email.trim(), name: name.trim(), initials: initials.trim(), role, ...(phone.trim() ? { phone: phone.trim() } : {}) } },
      {
        onSuccess: (u) => setDone(u),
        onError: (e) => {
          if (e instanceof ApiFailure) {
            setErrs(e.fieldErrors);
            if (e.code === 'conflict') setErrs({ email: 'Someone already has this email. Find them in the list instead.' });
          }
          setFormErr(errorText(e));
        },
      },
    );
  };

  if (done) {
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(done.sign_in_url);
        toast('Sign-in link copied');
      } catch {
        toast('Select the link and copy it');
      }
    };
    return (
      <Modal
        title={`${done.name} can sign in now`}
        sub="No email is sent. Share this link with them; they sign in with their Google work account."
        onClose={onClose}
        footer={
          <>
            <div className="grow" />
            <button className="btn" onClick={onClose}>
              Done
            </button>
            <button className="btn pri" onClick={copy}>
              Copy sign-in link
            </button>
          </>
        }
      >
        <Field label="Sign-in link">
          <input className="in" readOnly value={done.sign_in_url} onFocus={(e) => e.currentTarget.select()} />
        </Field>
        <div className="note good">
          {done.name} is set up as {ROLE_LABEL[done.role]}. Their menu and data follow that role from their first sign-in.
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Invite someone"
      sub="Add a person with an amplispot.com Google account and give them a role."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add person'}
          </button>
        </>
      }
    >
      {formErr ? <div className="note bad">{formErr}</div> : null}
      <Field label="Work email" error={errs.email}>
        <input className="in" type="email" autoComplete="off" placeholder="name@amplispot.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!errs.email || undefined} />
      </Field>
      <div className="cols c2">
        <Field label="Name" error={errs.name}>
          <input
            className="in"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!initialsTouched) setInitials(guessInitials(e.target.value));
            }}
            aria-invalid={!!errs.name || undefined}
          />
        </Field>
        <Field label="Initials" error={errs.initials}>
          <input
            className="in"
            maxLength={3}
            value={initials}
            onChange={(e) => {
              setInitialsTouched(true);
              setInitials(e.target.value.toUpperCase());
            }}
            aria-invalid={!!errs.initials || undefined}
          />
        </Field>
      </div>
      <Field label="Role" error={errs.role}>
        <select className="in" value={role} onChange={(e) => setRole(e.target.value as Role)} aria-invalid={!!errs.role || undefined}>
          <option value="">Choose a role</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Phone (optional)" error={errs.phone} hint="For WhatsApp messages. Country code and digits, for example +919820012345.">
        <input className="in" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} aria-invalid={!!errs.phone || undefined} />
      </Field>
    </Modal>
  );
}
